const FALLBACK_TIME_ZONES = [
  'UTC',
  'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
  'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Chicago',
  'America/Denver', 'America/Halifax', 'America/Lima', 'America/Los_Angeles', 'America/Mexico_City',
  'America/New_York', 'America/Phoenix', 'America/Sao_Paulo', 'America/Toronto', 'America/Vancouver',
  'Asia/Almaty', 'Asia/Baghdad', 'Asia/Bangkok', 'Asia/Dhaka', 'Asia/Dubai', 'Asia/Hong_Kong',
  'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Kolkata', 'Asia/Krasnoyarsk',
  'Asia/Manila', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tbilisi', 'Asia/Tehran',
  'Asia/Tokyo', 'Asia/Tomsk', 'Asia/Yerevan',
  'Atlantic/Reykjavik',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Brussels',
  'Europe/Bucharest', 'Europe/Budapest', 'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul',
  'Europe/Kyiv', 'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris',
  'Europe/Podgorica', 'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/Sofia', 'Europe/Stockholm',
  'Europe/Tallinn', 'Europe/Vienna', 'Europe/Vilnius', 'Europe/Warsaw', 'Europe/Zurich',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
] as const;

type IntlWithSupportedValues = typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
const RUSSIAN_ZONE_NAME_OVERRIDES: Record<string, string> = { 'Asia/Tomsk': 'Томск' };

export function isValidTimeZone(value: string): boolean {
  if (!value) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date()); return true; }
  catch { return false; }
}

export function detectedBrowserTimeZone(): string {
  try {
    const value = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return isValidTimeZone(value) ? value : '';
  } catch { return ''; }
}

export function supportedTimeZones(): string[] {
  let runtime: string[] = [];
  try { runtime = (Intl as IntlWithSupportedValues).supportedValuesOf?.('timeZone') || []; }
  catch { runtime = []; }
  const detected = detectedBrowserTimeZone();
  return [...new Set([...runtime, ...FALLBACK_TIME_ZONES, ...(detected ? [detected] : [])])]
    .filter(isValidTimeZone)
    .sort((left, right) => left.localeCompare(right));
}

export function filterTimeZones(zones: string[], query: string, selected = ''): string[] {
  const needle = query.trim().toLocaleLowerCase().replace(/[_/]+/g, ' ');
  const matches = needle ? zones.filter((zone) => zone.toLocaleLowerCase().replace(/[_/]+/g, ' ').includes(needle)) : zones;
  return selected && !matches.includes(selected) ? [selected, ...matches] : matches;
}

export interface LocalizedTimeZoneOption { value: string; label: string; searchText: string; offset: string }

function russianCityName(value: string): string {
  const words = value.replaceAll('_', ' ').toLowerCase().replace(/\bnew\b/g, 'нью').replace(/\bsaint\b/g, 'сент').replace(/\bst\b/g, 'сент');
  const pairs: Array<[RegExp, string]> = [
    [/sch/g, 'ш'], [/sh/g, 'ш'], [/ch/g, 'ч'], [/zh/g, 'ж'], [/kh/g, 'х'], [/ts/g, 'ц'], [/ya/g, 'я'], [/yu/g, 'ю'], [/ye/g, 'е'], [/ph/g, 'ф'], [/th/g, 'т'], [/ck/g, 'к'], [/qu/g, 'кв'],
  ];
  let result = words;
  pairs.forEach(([pattern, replacement]) => { result = result.replace(pattern, replacement); });
  const letters: Record<string, string> = { a: 'а', b: 'б', c: 'к', d: 'д', e: 'е', f: 'ф', g: 'г', h: 'х', i: 'и', j: 'дж', k: 'к', l: 'л', m: 'м', n: 'н', o: 'о', p: 'п', q: 'к', r: 'р', s: 'с', t: 'т', u: 'у', v: 'в', w: 'в', x: 'кс', y: 'й', z: 'з' };
  result = [...result].map((character) => letters[character] || character).join('');
  return result.replace(/(^|\s)\S/g, (character) => character.toLocaleUpperCase('ru'));
}

function localizedZoneName(zone: string, locale: 'en' | 'ru'): string {
  try {
    if (locale === 'ru' && RUSSIAN_ZONE_NAME_OVERRIDES[zone]) return RUSSIAN_ZONE_NAME_OVERRIDES[zone];
    const value = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value || '';
    if (locale === 'ru') {
      const cityStandard = value.match(/^(.+?),\s*(?:стандартное|летнее) время$/i)?.[1];
      if (cityStandard) return cityStandard;
      return russianCityName(zone.split('/').at(-1) || zone);
    }
    return (zone.split('/').at(-1) || value || zone).replaceAll('_', ' ');
  } catch { return zone.replaceAll('_', ' '); }
}

function zoneOffset(zone: string, locale: 'en' | 'ru'): string {
  try {
    const value = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value || 'GMT';
    return value.replace(/^GMT/i, 'UTC').replace(/^UTC$/, 'UTC+0');
  } catch { return 'UTC'; }
}

export function localizedTimeZoneOptions(locale: 'en' | 'ru', zones = supportedTimeZones()): LocalizedTimeZoneOption[] {
  return zones.map((value) => {
    const city = localizedZoneName(value, locale);
    const ianaCity = (value.split('/').at(-1) || value).replaceAll('_', ' ');
    const localizedIanaCity = locale === 'ru' ? russianCityName(ianaCity) : ianaCity;
    const offset = zoneOffset(value, locale);
    const cldr = (() => {
      try { return new Intl.DateTimeFormat(locale, { timeZone: value, timeZoneName: 'longGeneric' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value || ''; }
      catch { return ''; }
    })();
    return { value, offset, label: `${city} — ${offset}`, searchText: `${city} ${cldr} ${localizedIanaCity} ${value.replaceAll('_', ' ')} ${offset}`.toLocaleLowerCase(locale) };
  }).sort((left, right) => left.label.localeCompare(right.label, locale));
}

export function filterLocalizedTimeZones(options: LocalizedTimeZoneOption[], query: string, selected = '', locale: 'en' | 'ru' = 'en'): LocalizedTimeZoneOption[] {
  const normalize = (value: string) => value.trim().toLocaleLowerCase(locale).replaceAll('ё', 'е').replace(/[_/\p{P}\p{S}]+/gu, ' ').replace(/\s+/g, ' ');
  const needle = normalize(query);
  const matches = needle ? options.filter((option) => normalize(option.searchText).includes(needle)) : options;
  const selectedOption = options.find((option) => option.value === selected);
  return selectedOption && !matches.some((option) => option.value === selected) ? [selectedOption, ...matches] : matches;
}
